import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { API_BASE_URL } from "../lib/env";
import { verifyTurnstileViaWorker } from "../lib/turnstile-siteverify";
import { Button } from "./ui";
import { TurnstileWidget } from "./TurnstileWidget";

const DemoSchema = z.object({
  name: z.string().min(2, "Please enter your name").max(120),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().max(40).optional().or(z.literal("")),
  organization: z.string().min(2, "Organization required").max(200),
  facility_type: z.enum(["snf", "asc", "dialysis", "other"], {
    errorMap: () => ({ message: "Pick a facility type" }),
  }),
  bed_count: z.string().max(20).optional().or(z.literal("")),
  message: z.string().max(2000).optional().or(z.literal("")),
  sms_consent: z.boolean().optional(),
  website: z.string().max(200).optional().or(z.literal("")), // honeypot
}).refine(
  (data) => {
    // TCPA: if phone provided, sms_consent must be explicit if intending SMS.
    // Form accepts phone w/o SMS consent (for voice contact only).
    // SMS contact requires sms_consent === true.
    return true;
  },
  { message: "" }
);

export type DemoFormData = z.infer<typeof DemoSchema>;

const inputCls =
  "block w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 focus:border-forest-700";

const labelCls =
  "block text-sm font-semibold text-ink-800 mb-1.5";

export function DemoForm({
  source = "demo",
  endpoint = "demo-request",
}: {
  source?: string;
  endpoint?: "demo-request" | "contact";
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<DemoFormData>({
    resolver: zodResolver(DemoSchema),
    defaultValues: {
      facility_type: "snf",
      website: "",
    },
  });
  const [status, setStatus] = useState<"idle" | "ok" | "err">("idle");
  const [captchaToken, setCaptchaToken] = useState("");

  async function onSubmit(data: DemoFormData) {
    const spin = await verifyTurnstileViaWorker(captchaToken);
    if (!spin.success) {
      setStatus("err");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/v1/marketing/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...data, source, captcha_token: captchaToken }),
      });
      if (!res.ok) throw new Error(`status_${res.status}`);
      setStatus("ok");
      reset();
      setCaptchaToken("");
    } catch {
      setStatus("err");
    }
  }

  if (status === "ok") {
    return (
      <div
        role="status"
        className="rounded-2xl border border-forest-200 bg-forest-50 p-8 text-center"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-forest-700" aria-hidden />
        <h3 className="mt-4 text-xl font-bold text-ink-900">Thanks — we’ll be in touch.</h3>
        <p className="mt-2 text-ink-700">
          A member of the WeConsult Rx team will reach out within one business day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      {/* Honeypot */}
      <label className="sr-only" aria-hidden="true">
        Website
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register("website")}
          style={{ display: "none", position: "absolute", left: "-9999px" }}
        />
      </label>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="name" className={labelCls}>
            Full name
          </label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            aria-invalid={!!errors.name}
            className={inputCls}
            {...register("name")}
          />
          {errors.name ? <p className="mt-1 text-xs text-red-600">{errors.name.message}</p> : null}
        </div>
        <div>
          <label htmlFor="email" className={labelCls}>
            Work email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            className={inputCls}
            {...register("email")}
          />
          {errors.email ? <p className="mt-1 text-xs text-red-600">{errors.email.message}</p> : null}
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="phone" className={labelCls}>
            Phone (optional)
          </label>
          <input
            id="phone"
            type="tel"
            autoComplete="tel"
            className={inputCls}
            {...register("phone")}
          />
        </div>
        <div>
          <label htmlFor="organization" className={labelCls}>
            Organization
          </label>
          <input
            id="organization"
            type="text"
            autoComplete="organization"
            aria-invalid={!!errors.organization}
            className={inputCls}
            {...register("organization")}
          />
          {errors.organization ? (
            <p className="mt-1 text-xs text-red-600">{errors.organization.message}</p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="facility_type" className={labelCls}>
            Primary facility type
          </label>
          <select
            id="facility_type"
            aria-invalid={!!errors.facility_type}
            className={inputCls}
            {...register("facility_type")}
          >
            <option value="snf">Skilled nursing (SNF)</option>
            <option value="asc">Ambulatory surgical center (ASC)</option>
            <option value="dialysis">Dialysis</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label htmlFor="bed_count" className={labelCls}>
            Bed count / annual cases (optional)
          </label>
          <input id="bed_count" type="text" className={inputCls} {...register("bed_count")} />
        </div>
      </div>

      <div>
        <label htmlFor="message" className={labelCls}>
          Anything we should know? (optional)
        </label>
        <textarea
          id="message"
          rows={5}
          className={inputCls}
          {...register("message")}
        />
      </div>

      <div className="rounded-lg border border-border bg-surface-muted px-4 py-3.5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-border accent-forest-700"
            {...register("sms_consent")}
          />
          <span className="text-xs text-ink-600 leading-relaxed">
            <strong className="text-ink-800">SMS opt-in (optional).</strong>{" "}
            I agree to receive transactional and account-related SMS messages
            from WeConsult Rx at the phone number above. Message frequency
            varies. Message and data rates may apply. Reply STOP to opt out or
            HELP for help. See our{" "}
            <a href="/sms-terms" className="underline">SMS Terms</a> and{" "}
            <a href="/privacy" className="underline">Privacy Policy</a>. No
            mobile information is shared with third parties or affiliates for
            marketing.
          </span>
        </label>
      </div>

      <TurnstileWidget onToken={setCaptchaToken} />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Button type="submit" disabled={isSubmitting} size="lg">
          {isSubmitting ? "Sending…" : "Request a demo"}
        </Button>
        <p className="text-xs text-ink-500">
          By submitting you agree to our{" "}
          <a href="/privacy" className="underline">privacy policy</a> and{" "}
          <a href="/terms" className="underline">terms of service</a>. No data
          sold to third parties.
        </p>
      </div>

      {status === "err" ? (
        <p role="alert" className="text-sm text-red-600">
          Something went wrong. Please email{" "}
          <a href="mailto:hello@weconsultrx.com" className="underline">hello@weconsultrx.com</a>.
        </p>
      ) : null}
    </form>
  );
}
